'use client';

import * as ENGINE from '@gnsx/genesys.js';
import * as THREE from 'three';

import { SceneBackgroundColors } from '@/editor/core/sceneBackgroundColors';

export interface EditorWorldActorHooks {
  onActorAddToWorld: (actor: ENGINE.Actor) => void | Promise<void>;
  onActorRemoveFromWorld: (actor: ENGINE.Actor) => void;
}

class EditorRenderer extends ENGINE.Renderer {
  constructor(nativeRenderer: ENGINE.IGenesysRenderer) {
    super(nativeRenderer.domElement, nativeRenderer);
  }

  protected override initializeRenderer(): void {
    // Do nothing since we don't own the native renderer
  }

  public override destroy(): void {
    // Do nothing since we don't own the native renderer
  }
}

/**
 * Session-scoped game loop for the editor viewport.
 * Owns durable subsystems (physics, navigation, manifold) and transient {@link ENGINE.World} instances.
 */
export class EditorGameLoop extends ENGINE.BaseGameLoop {
  private editorFrameNumber = 0;

  constructor(container: HTMLElement = document.body) {
    super(container, {
      backgroundColor: SceneBackgroundColors.default,
      debugUIMode: 'auto',
    });
  }

  public override getWorldConfiguration(): ENGINE.WorldOptions {
    const base = super.getWorldConfiguration();
    return {
      ...base,
      backgroundColor: SceneBackgroundColors.default,
      physicsOptions: {
        gravity: ENGINE.MathHelpers.makeVector({ up: -9.81 }),
        engine: ENGINE.PhysicsEngine.Rapier,
      },
      navigationOptions: {
        engine: ENGINE.NavigationEngine.RecastNavigation,
        debug: false,
        generateOnStartUp: false,
      },
    };
  }

  /** Initializes the durable editor session with the R3F-owned renderer. */
  public initSession(nativeRenderer: ENGINE.IGenesysRenderer): void {
    this.renderer = new EditorRenderer(nativeRenderer);
  }

  @THREE.profile
  public tickInEditor(deltaTime: number): void {
    this.getWorld()?.tickInEditor(deltaTime);
    this.rootScene.tickInEditor(deltaTime);
  }

  public toggleDebugUI(enable: boolean): void {
    if (enable) {
      if (this.renderer) {
        this.createDebugUI(this.renderer, true);
      } else {
        console.error('[EditorGameLoop] Cannot create debug UI: renderer not initialized');
      }
    } else {
      this.disposeDebugUI();
    }
  }

  /**
   * Replaces the active editor world with deserialized level data (or an empty world).
   * Mirrors {@link ENGINE.BaseGameLoop.openLevel} teardown/load semantics without {@link ENGINE.World.beginPlay}.
   */
  public async loadEditorLevel(
    levelData: ENGINE.SerializedData | undefined,
    hooks: EditorWorldActorHooks,
    prefabs?: Record<string, ENGINE.SerializedData>,
  ): Promise<ENGINE.World> {
    await this.unloadCurrentLevel();

    const pendingOnActorAddToWorld = new Set<ENGINE.Actor>();
    let isHydratingWorld = !!levelData;

    const applyOnActorAddToWorld = async (actor: ENGINE.Actor) => {
      await hooks.onActorAddToWorld(actor);
    };

    let world: ENGINE.World;
    if (levelData) {
      const gameMode = this.resolveStartupGameMode(levelData);
      this._world = await this.createWorld({
        ...this.getWorldConfiguration(),
        gameId: this.options.gameId,
        gameMode,
        gameLoop: new WeakRef(this),
      });
      world = this._world;
    } else {
      world = await this.createEmptyLevelWorld();
    }

    world.setIsEditorWorld(
      true,
      async (actor: ENGINE.Actor) => {
        if (isHydratingWorld) {
          pendingOnActorAddToWorld.add(actor);
          return;
        }
        await applyOnActorAddToWorld(actor);
      },
      hooks.onActorRemoveFromWorld,
    );

    world.scene.name = 'Scene';

    // CSG runs during editor deserialization (onEditorAddToWorld); manifold must exist first.
    await this.createManifold();

    if (levelData) {
      world.prepareLoadedPrefabs(prefabs ?? {});
      await ENGINE.WorldSerializer.loadWorld(world, levelData, true);
      isHydratingWorld = false;
    }

    for (const actor of pendingOnActorAddToWorld) {
      await applyOnActorAddToWorld(actor);
    }
    pendingOnActorAddToWorld.clear();

    await this.enterEditMode();
    console.log('[EditorGameLoop] Loaded editor level', world.uuid);
    return world;
  }
}
